import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Store, Package, Users, ShoppingBag, BarChart3,
  Shield, Zap, Globe, Check, ArrowRight, ChevronDown
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtIDR = (v) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(v || 0);
const fmtUSD = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);

const features = [
  { icon: Store, title: 'Multi-Store', desc: 'Kelola beberapa toko dari satu dashboard terpusat.' },
  { icon: Package, title: 'Inventori Cerdas', desc: 'Pantau stok real-time, stock opname, dan stock-in otomatis.' },
  { icon: Users, title: 'Manajemen Karyawan', desc: 'Undang karyawan via email, atur peran & hak akses.' },
  { icon: ShoppingBag, title: 'POS Modern', desc: 'Sistem kasir cepat dengan dukungan berbagai tipe produk.' },
  { icon: BarChart3, title: 'Laporan & Analitik', desc: 'Dashboard penjualan harian, produk terlaris, dan statistik.' },
  { icon: Shield, title: 'Keamanan Data', desc: 'Isolasi data per admin, enkripsi AES-256 untuk API mobile.' },
];

export default function LandingPage() {
  const [packages, setPackages] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(0);
  const [showUSD, setShowUSD] = useState(false);
  const [loadingPkg, setLoadingPkg] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [pkgRes, rateRes] = await Promise.all([
          axios.get(`${API}/api/public/packages`),
          axios.get(`${API}/api/public/exchange-rate`)
        ]);
        setPackages(pkgRes.data);
        setExchangeRate(rateRes.data.rate || 0);
      } catch (err) { console.error(err); }
      finally { setLoadingPkg(false); }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#FEFEFE]" data-testid="landing-page">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-[#FEFEFE]/90 backdrop-blur-md border-b border-[#DFDEDE]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#EB7B44] flex items-center justify-center">
              <span className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope' }}>S</span>
            </div>
            <span className="text-xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>Sellmu</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-[#64584F] hover:text-[#202020] font-medium" data-testid="landing-login-btn">
                Masuk
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#EB7B44] hover:bg-[#D96A35] text-white font-semibold rounded-lg px-5" data-testid="landing-register-btn">
                Daftar Gratis
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#EB7B44]/5 via-transparent to-[#EB7B44]/3" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-24 text-center relative">
          <Badge className="bg-[#EB7B44]/10 text-[#EB7B44] border-[#EB7B44]/20 mb-6 px-4 py-1.5 text-sm font-medium">
            <Zap size={14} className="mr-1.5" /> Sistem POS Modern
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#202020] leading-tight mb-6" style={{ fontFamily: 'Manrope' }}>
            Kelola Bisnis Anda
            <br />
            <span className="text-[#EB7B44]">Lebih Mudah</span>
          </h1>
          <p className="text-base sm:text-lg text-[#64584F] max-w-2xl mx-auto mb-10 leading-relaxed">
            Sellmu POS membantu Anda mengelola transaksi, inventori, karyawan, dan operasional bisnis ritel & jasa dalam satu platform terintegrasi.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-[#EB7B44] hover:bg-[#D96A35] text-white font-semibold rounded-xl px-8 h-12 text-base" data-testid="hero-register-btn">
                Mulai Sekarang <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <a href="#pricing">
              <Button size="lg" variant="outline" className="border-[#CACACC] text-[#64584F] hover:text-[#202020] hover:border-[#EB7B44] rounded-xl px-8 h-12 text-base">
                Lihat Harga <ChevronDown size={18} className="ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-[#F6F5F5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-base sm:text-lg font-bold text-[#202020] mb-3" style={{ fontFamily: 'Manrope' }}>
              Fitur Lengkap untuk Bisnis Anda
            </h2>
            <p className="text-sm text-[#64584F] max-w-xl mx-auto">
              Semua yang Anda butuhkan untuk mengelola operasional bisnis, dari kasir hingga laporan.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <Card key={i} className="bg-white border-[#DFDEDE] rounded-xl hover:shadow-md hover:border-[#EB7B44]/30 transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="w-11 h-11 rounded-xl bg-[#EB7B44]/10 flex items-center justify-center mb-4 group-hover:bg-[#EB7B44]/20 transition-colors">
                    <f.icon size={22} className="text-[#EB7B44]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-bold text-[#202020] mb-2" style={{ fontFamily: 'Manrope' }}>{f.title}</h3>
                  <p className="text-sm text-[#64584F] leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-base sm:text-lg font-bold text-[#202020] mb-3" style={{ fontFamily: 'Manrope' }}>
              Pilih Paket yang Tepat
            </h2>
            <p className="text-sm text-[#64584F] max-w-xl mx-auto mb-6">
              Mulai gratis dan upgrade kapanpun sesuai kebutuhan bisnis Anda.
            </p>
            {exchangeRate > 0 && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setShowUSD(false)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${!showUSD ? 'bg-[#EB7B44] text-white' : 'bg-[#F6F5F5] text-[#64584F] hover:text-[#202020]'}`}
                  data-testid="currency-idr-btn"
                >
                  IDR
                </button>
                <button
                  onClick={() => setShowUSD(true)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${showUSD ? 'bg-[#EB7B44] text-white' : 'bg-[#F6F5F5] text-[#64584F] hover:text-[#202020]'}`}
                  data-testid="currency-usd-btn"
                >
                  <Globe size={14} className="inline mr-1.5" />USD
                </button>
              </div>
            )}
          </div>

          {loadingPkg ? (
            <div className="text-center py-12 text-[#A6A2A0]">Memuat paket...</div>
          ) : packages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#A6A2A0] mb-2">Belum ada paket tersedia.</p>
              <p className="text-sm text-[#CACACC]">Hubungi admin untuk informasi lebih lanjut.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {packages.map((pkg, i) => (
                <Card
                  key={pkg.id}
                  className={`rounded-xl border-2 transition-all duration-300 hover:shadow-lg ${
                    i === 1 ? 'border-[#EB7B44] shadow-md relative' : 'border-[#DFDEDE] hover:border-[#EB7B44]/40'
                  }`}
                  data-testid={`pricing-card-${pkg.id}`}
                >
                  {i === 1 && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-[#EB7B44] text-white border-0 px-3 py-0.5 text-xs font-semibold">Populer</Badge>
                    </div>
                  )}
                  <CardContent className="p-6 text-center">
                    <h3 className="text-base font-bold text-[#202020] mb-2" style={{ fontFamily: 'Manrope' }}>{pkg.name}</h3>
                    {pkg.description && <p className="text-xs text-[#64584F] mb-4">{pkg.description}</p>}
                    <div className="mb-6">
                      <span className="text-3xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>
                        {showUSD ? fmtUSD(pkg.price_idr * exchangeRate) : fmtIDR(pkg.price_idr)}
                      </span>
                      <span className="text-sm text-[#A6A2A0] ml-1">/bulan</span>
                    </div>
                    <div className="text-left space-y-3 mb-6">
                      <div className="flex items-center gap-2.5 text-sm text-[#64584F]">
                        <Check size={16} className="text-[#EB7B44] flex-shrink-0" />
                        <span>{pkg.store_limit} toko</span>
                      </div>
                      {(pkg.features || []).map((f, fi) => (
                        <div key={fi} className="flex items-center gap-2.5 text-sm text-[#64584F]">
                          <Check size={16} className="text-[#EB7B44] flex-shrink-0" />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>
                    <Link to="/register">
                      <Button
                        className={`w-full rounded-lg font-semibold ${
                          i === 1
                            ? 'bg-[#EB7B44] hover:bg-[#D96A35] text-white'
                            : 'bg-white border-2 border-[#EB7B44] text-[#EB7B44] hover:bg-[#EB7B44] hover:text-white'
                        }`}
                        data-testid={`pricing-cta-${pkg.id}`}
                      >
                        Pilih Paket
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0f172a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-base sm:text-lg font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Siap Memulai?
          </h2>
          <p className="text-sm text-white/60 mb-8">
            Daftar sekarang dan mulai kelola bisnis Anda dengan Sellmu POS.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-[#EB7B44] hover:bg-[#D96A35] text-white font-semibold rounded-xl px-8 h-12 text-base" data-testid="cta-register-btn">
              Daftar Gratis Sekarang <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[#DFDEDE] bg-[#FEFEFE]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#EB7B44] flex items-center justify-center">
              <span className="text-white font-bold text-sm" style={{ fontFamily: 'Manrope' }}>S</span>
            </div>
            <span className="font-bold text-[#202020] text-sm" style={{ fontFamily: 'Manrope' }}>Sellmu POS</span>
          </div>
          <p className="text-xs text-[#A6A2A0]">&copy; 2026 Sellmu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
